import { ServerStackIcon } from "@heroicons/react/24/outline";
import { FileOrganizerProvider } from "@via/context/file-organizer-context.tsx";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
import DataSourceTitle from "@via/pages/data/data-sources/data-source-title.tsx";
import type { BucketObject } from "@via/pages/data/data-sources/utils/helpers.tsx";
import FileOrganizer from "@via/pages/data/file-organizer/file-organizer.tsx";
import { DataSourceComponentView } from "@via/utils/interfaces.tsx";
import {
  DataSourceProvider,
  parseRemoteStorageUri,
  REMOTE_STORAGE_REFUSAL_MESSAGES,
  type SelectedFile,
} from "common";
import { type JSX, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import RemoteStorageFolderBrowser from "./remote-storage-folder-browser.tsx";
import {
  remoteStorageErrorMessage,
  useRecheckHostKey,
  useRemoteStorageCredentials,
  useSaveRemoteStorageCredential,
} from "./remote-storage.api.tsx";
import { useOAuthConnections } from "./remote-storage-oauth.api.tsx";

/** /data/remote-storage: browse a saved remote server, then organize the selected files (data-1-2). */
export default function RemoteStorageData(): JSX.Element {
  const [searchParams] = useSearchParams();
  const { data: bookmarks } = useGetDataSourceList();
  const { data: credentials } = useRemoteStorageCredentials();
  const { data: oauth } = useOAuthConnections();
  const recheck = useRecheckHostKey();
  const save = useSaveRemoteStorageCredential();
  const [view, setView] = useState(DataSourceComponentView.FolderBrowser);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [cart, setCart] = useState<BucketObject[]>([]);
  const [keyChanged, setKeyChanged] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [browseKey, setBrowseKey] = useState(0);

  const location = useMemo(() => {
    const bookmarkId = Number(searchParams.get("bookmark"));
    if (bookmarkId) {
      const bookmark = bookmarks?.find(item => item.id === bookmarkId);
      return bookmark ? parseRemoteStorageUri(bookmark.path) : null;
    }
    const credentialId = Number(searchParams.get("credentialId"));
    return credentialId > 0 ? { credentialId, path: "" } : null;
  }, [searchParams, bookmarks]);

  if (!location) {
    return (
      <p className="p-6 text-sm text-gray-600">
        Choose a remote server on Connect Data Source first.
      </p>
    );
  }
  const summary = credentials?.credentials.find(
    c => c.id === location.credentialId
  );

  const acceptNewKey = async () => {
    if (!summary) return;
    setKeyError(null);
    try {
      const found = await recheck.mutateAsync(summary.id);
      if (
        window.confirm(
          `The server now presents ${found.fingerprint}. Does this match the fingerprint its administrator gave you?`
        )
      ) {
        await save.mutateAsync({
          id: summary.id,
          body: {
            name: summary.name,
            protocol: summary.protocol,
            variables: summary.variables,
            secrets: {},
            hostKey: found.hostKey,
          },
        });
        setKeyChanged(false);
        setBrowseKey(key => key + 1);
      }
    } catch (failure) {
      setKeyError(remoteStorageErrorMessage(failure));
    }
  };

  return (
    <div className="flex-auto">
      <div className="ml-2">
        <DataSourceTitle
          icon={<ServerStackIcon className="mr-3 h-10 w-10" />}
          title={
            summary?.name ??
            oauth?.connections.find(c => c.id === location.credentialId)
              ?.name ??
            "Remote server"
          }
        />
        {keyChanged ? (
          <div className="my-2">
            <p className="text-sm text-rose-600" role="alert">
              {REMOTE_STORAGE_REFUSAL_MESSAGES.host_key_changed}
            </p>
            {keyError && (
              <p className="mt-1 text-sm text-rose-600" role="alert">
                {keyError}
              </p>
            )}
            <button
              type="button"
              onClick={() => void acceptNewKey()}
              className="mt-2 rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300">
              Review the new host key
            </button>
          </div>
        ) : view === DataSourceComponentView.FileOrganizer ? (
          <FileOrganizerProvider>
            <FileOrganizer
              importedFiles={selectedFiles}
              globalBucketName={`remote://${location.credentialId}`}
              onBackToSelectionClick={() =>
                setView(DataSourceComponentView.FolderBrowser)
              }
              credentialId={null}
              provider={DataSourceProvider.RemoteStorage}
            />
          </FileOrganizerProvider>
        ) : (
          <RemoteStorageFolderBrowser
            key={browseKey}
            credentialId={location.credentialId}
            startPath={location.path}
            cart={cart}
            setCart={setCart}
            onHostKeyChanged={() => setKeyChanged(true)}
            onContinue={(files: unknown) => {
              setSelectedFiles(
                (files as SelectedFile[]).map(file => ({
                  ...file,
                  id: file.name,
                }))
              );
              setView(DataSourceComponentView.FileOrganizer);
            }}
          />
        )}
      </div>
    </div>
  );
}
